import { useState, useMemo } from 'react';
import { B, font, radius, fonts, weight } from '../constants/brand';
import { api } from '../api/client';
import { fmt } from '../utils/format';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Table, PageHeader, Icon } from '../components/ui';
import { todayIsoInZone } from '@harvus/shared';

// Acres to display: prefer the user-entered acres, fall back to Regrid acreage.
const acresOf = (p) => (p.acres != null && p.acres !== 0) ? p.acres
                     : (p.regridAcres != null ? p.regridAcres : null);
const fmtAcres = (v) => v != null ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';
const dealLabel = (p) => [p.dealName, p.detailName || p.detailType].filter(Boolean).join(' / ') || '—';

// Report columns — single source of truth for the table and the CSV export.
const COLUMNS = [
  { key: 'county',        label: 'County',          value: p => p.county || '' },
  { key: 'state',         label: 'State',           value: p => p.state || '' },
  { key: 'parcelNumber',  label: 'Parcel Number',   value: p => p.parcelNumber || '' },
  { key: 'ppin',          label: 'PPIN',            value: p => p.ppin || '' },
  { key: 'purchasedDate', label: 'Date Purchased',  value: p => fmt.date(p.purchasedDate), csv: p => p.purchasedDate ? p.purchasedDate.slice(0, 10) : '' },
  { key: 'soldDate',      label: 'Date Sold',       value: p => fmt.date(p.soldDate),      csv: p => p.soldDate ? p.soldDate.slice(0, 10) : '' },
  { key: 'acres',         label: 'Acres',           value: p => fmtAcres(acresOf(p)), align: 'right', csv: p => acresOf(p) ?? '' },
  { key: 'deal',          label: 'Deal / Detail',   value: p => dealLabel(p) },
];

function exportCsv(rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = COLUMNS.map(c => c.label).join(',');
  const body = rows.map(p => COLUMNS.map(c => esc((c.csv || c.value)(p))).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `parcel-report-${todayIsoInZone()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const ParcelReport = ({ onDealClick }) => {
  const { data: parcels, loading, error, reload } = useApi(() => api.get('parcels'), []);
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const all = parcels || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(p =>
      [p.county, p.state, p.parcelNumber, p.ppin, p.dealName, p.detailName, p.detailType]
        .some(v => (v || '').toString().toLowerCase().includes(needle))
    );
  }, [parcels, q]);

  const totalAcres = useMemo(
    () => rows.reduce((s, p) => s + (Number(acresOf(p)) || 0), 0),
    [rows]
  );

  // Shared-Table columns: render deal cell as a link when clickable.
  const tableColumns = COLUMNS.map(c => ({
    key: c.key,
    label: c.label,
    align: c.align,
    render: (_v, p) => (
      c.key === 'deal' && p.dealID && onDealClick ? (
        <button onClick={() => onDealClick(p.dealID)}
          style={{ background: 'none', border: 'none', padding: 0, color: B.navy, cursor: 'pointer', fontFamily: 'inherit', fontSize: font.base, fontWeight: weight.medium, textAlign: 'left', textDecoration: 'underline' }}>
          {c.value(p)}
        </button>
      ) : c.value(p)
    ),
  }));

  return (
    <div>
      <PageHeader
        icon="parcelReport"
        title="Parcel Report"
        subtitle={`${rows.length} parcel${rows.length !== 1 ? 's' : ''} · ${fmtAcres(totalAcres)} total acres`}
        actions={
          <>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 10, display: 'flex', color: B.textLt, pointerEvents: 'none' }}><Icon name="search" size={15} /></span>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search county, APN, deal…"
                style={{ padding: '8px 12px 8px 32px', borderRadius: radius.md, border: `1px solid ${B.border}`, fontSize: font.base, minWidth: 220, fontFamily: fonts.sans, background: B.surface, outline: 'none' }}
              />
            </div>
            <Btn size="sm" variant="ghost" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
            <Btn size="sm" disabled={rows.length === 0} onClick={() => exportCsv(rows)}><Icon name="download" size={14} /> Export CSV</Btn>
          </>
        }
      />

      {error ? <ErrorMsg msg={error} onRetry={reload} /> : loading ? <Spinner /> : (
        <Table columns={tableColumns} data={rows} />
      )}
    </div>
  );
};
